import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, Mail, Plus } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useAuthStore, useActiveOrganisation } from '@/stores/auth';
import { useSwitchOrg } from '@/hooks/useSwitchOrg';
import { PageHeader } from '@/app/DashboardLayout';
import { NewBookSheet } from '@/features/books/NewBookSheet';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, ColourDot, ErrorState, Input, SkeletonTable } from '@/features/common/ui';

/**
 * Organisations: the shelves the person's books stand on.
 *
 * Every organisation the person belongs to, with the books they can see in
 * it. The owner renames it, puts a new book on it, moves a book they
 * created between the shelves they own, and invites one person into every
 * book on it at once - the CA across both books, in one go. Someone who is
 * not the owner sees the shelf and its books and no controls: the shelf is
 * not theirs to rearrange.
 */
interface OrganisationBook {
  id: string;
  name: string;
  colour: string;
  roleName: string;
  roleKey: string | null;
  mine: boolean;
}
interface PendingInvitation {
  id: string;
  email: string;
  roleKey: string;
}
interface Organisation {
  id: string;
  name: string;
  slug: string;
  colour: string;
  owner: boolean;
  ownerName: string | null;
  books: OrganisationBook[];
  /** Waiting for this shelf's first book: a shelf with no books can still be invited into. */
  pendingInvitations?: PendingInvitation[];
}
interface InviteOutcome {
  email: string;
  roleKey: string;
  books: Array<{ orgId: string; name: string; outcome: 'invited' | 'already' | 'refused'; otp?: string; link?: string; reason?: string }>;
  /** Set instead of `books` when the shelf had no book to put them in yet. */
  pending?: { id: string; email: string; roleKey: string } | null;
}

const SEAT: Record<string, string> = { auditor: 'Auditor', member: 'Member', admin: 'Administrator' };

export function OrganisationPage() {
  const queryClient = useQueryClient();
  const active = useActiveOrganisation();
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [bookIn, setBookIn] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['organisations'],
    queryFn: () => api.get<{ items: Organisation[] }>('/organisations'),
  });
  // The switcher reads the session; every change here is a change to it.
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['organisations'] });
    void queryClient.invalidateQueries({ queryKey: ['profile'] });
  };

  const create = useMutation({
    mutationFn: () => api.post<Organisation>('/organisations', { name: newName.trim() }),
    onSuccess: () => {
      setCreating(false);
      setNewName('');
      refresh();
    },
  });

  const items = list.data?.items ?? [];
  const owned = items.filter((o) => o.owner);

  return (
    <>
      <PageHeader
        title="Organisations"
        subtitle="The shelves your books stand on. An organisation's books are added up together on the Overview; a demo shelf and a real one are never added together."
        actions={
          <>
            <Button size="sm" variant="ghost" asChild>
              <Link to="/settings">
                <ArrowLeft className="size-4" aria-hidden />
                Settings
              </Link>
            </Button>
            <Button size="sm" onClick={() => setCreating(true)} data-testid="organisation-new">
              <Plus className="size-4" aria-hidden />
              New organisation
            </Button>
          </>
        }
      />
      <div className="max-w-3xl space-y-4 px-4 pb-10 sm:px-5">
        {creating ? (
          <Card>
            <CardHeader>
              <CardTitle>New organisation</CardTitle>
              <CardDescription>An empty shelf, yours. Put a book on it with "New book here", or move one of yours onto it.</CardDescription>
            </CardHeader>
            <CardContent>
              <form
                className="flex flex-wrap items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (newName.trim()) create.mutate();
                }}
              >
                <div className="min-w-[16rem] flex-1">
                  <Input
                    label="Name"
                    autoFocus
                    required
                    maxLength={120}
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Demo, or the group's name"
                    error={create.error instanceof ApiError ? create.error.fieldError('name') : undefined}
                    data-testid="organisation-name"
                  />
                </div>
                <Button type="submit" loading={create.isPending} data-testid="organisation-create">
                  Create
                </Button>
                <Button type="button" variant="ghost" onClick={() => setCreating(false)}>
                  Cancel
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : null}

        {list.isError ? <ErrorState error={list.error} onRetry={() => { void list.refetch(); }} /> : null}
        {list.isPending ? <SkeletonTable rows={3} /> : null}
        {items.map((organisation) => (
          <OrganisationCard
            key={organisation.id}
            organisation={organisation}
            isActive={organisation.id === active?.id}
            activeOrgId={activeOrgId}
            owned={owned}
            onChanged={refresh}
            onNewBook={() => setBookIn(organisation.id)}
          />
        ))}
      </div>
      {bookIn ? <NewBookSheet onClose={() => setBookIn(null)} organisationId={bookIn} /> : null}
    </>
  );
}

function OrganisationCard({
  organisation,
  isActive,
  activeOrgId,
  owned,
  onChanged,
  onNewBook,
}: {
  organisation: Organisation;
  isActive: boolean;
  activeOrgId: string | null;
  owned: Organisation[];
  onChanged: () => void;
  onNewBook: () => void;
}) {
  const switchTo = useSwitchOrg();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(organisation.name);
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState('');
  const [roleKey, setRoleKey] = useState<'auditor' | 'member' | 'admin'>('auditor');
  const [invited, setInvited] = useState<InviteOutcome | null>(null);

  const rename = useMutation({
    mutationFn: () => api.patch<Organisation>(`/organisations/${organisation.id}`, { name: name.trim() }),
    onSuccess: () => {
      setRenaming(false);
      onChanged();
    },
  });
  const move = useMutation({
    mutationFn: (input: { orgId: string; organisationId: string }) => api.patch(`/orgs/${input.orgId}/organisation`, { organisationId: input.organisationId }),
    onSuccess: onChanged,
  });
  const invite = useMutation({
    mutationFn: () => api.post<InviteOutcome>(`/organisations/${organisation.id}/members`, { email: email.trim(), roleKey }),
    onSuccess: (data) => {
      setInvited(data);
      setEmail('');
      // A held invitation is a row on the shelf, so the list has changed.
      if (data.pending) onChanged();
    },
  });
  const revoke = useMutation({
    mutationFn: (invitationId: string) => api.delete(`/organisations/${organisation.id}/members/pending/${invitationId}`),
    onSuccess: onChanged,
  });
  const waiting = organisation.pendingInvitations ?? [];
  const others = owned.filter((o) => o.id !== organisation.id);

  return (
    <Card data-testid="organisation-card" data-active={isActive ? 'true' : undefined}>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            {renaming ? (
              <form
                className="flex flex-wrap items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (name.trim()) rename.mutate();
                }}
              >
                <Input label="Name" autoFocus required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} data-testid="organisation-rename" />
                <Button type="submit" size="sm" loading={rename.isPending}>
                  Save
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => { setRenaming(false); setName(organisation.name); }}>
                  Cancel
                </Button>
              </form>
            ) : (
              <CardTitle className="flex items-center gap-2">
                <ColourDot colour={organisation.colour} className="h-2.5 w-2.5" />
                <span className="truncate" data-testid="organisation-title">{organisation.name}</span>
                {isActive ? <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">current</span> : null}
              </CardTitle>
            )}
            <CardDescription>
              {organisation.owner ? 'Yours.' : organisation.ownerName ? `Owned by ${organisation.ownerName}.` : 'Owned by someone else.'}{' '}
              {organisation.books.length === 0 ? 'No books yet.' : `${organisation.books.length} ${organisation.books.length === 1 ? 'book' : 'books'} you can see.`}
            </CardDescription>
          </div>
          {organisation.owner && !renaming ? (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setRenaming(true)} data-testid="organisation-rename-open">
                Rename
              </Button>
              <Button size="sm" variant="outline" onClick={onNewBook} data-testid="organisation-new-book">
                <Plus className="size-4" aria-hidden />
                New book here
              </Button>
              <Button size="sm" variant="outline" onClick={() => setInviting((v) => !v)} aria-expanded={inviting} data-testid="organisation-invite-open">
                <Mail className="size-4" aria-hidden />
                {organisation.books.length === 0 ? 'Invite' : 'Invite to every book'}
              </Button>
            </div>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {organisation.books.length ? (
          <ul className="divide-y divide-border rounded-md border border-border">
            {organisation.books.map((book) => (
              <li key={book.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm" data-testid="organisation-book">
                <button type="button" className="flex min-w-0 items-center gap-2 text-left hover:underline" onClick={() => switchTo(book.id)}>
                  <ColourDot colour={book.colour} className="h-2 w-2" />
                  <span className="truncate font-medium">{book.name}</span>
                  <span className="text-xs text-muted-foreground">· {book.roleName}</span>
                  {book.id === activeOrgId ? <span className="text-xs text-muted-foreground">(open)</span> : null}
                </button>
                {book.mine && others.length > 0 ? (
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    Move to
                    <select
                      className="min-h-8 rounded-md border border-input bg-background px-2 text-xs"
                      value=""
                      onChange={(e) => {
                        if (e.target.value) move.mutate({ orgId: book.id, organisationId: e.target.value });
                      }}
                      aria-label={`Move ${book.name} to another organisation`}
                      data-testid="organisation-move"
                    >
                      <option value="">…</option>
                      {others.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            {organisation.owner ? 'Nothing on this shelf yet. "New book here" starts one; "Move to" on one of your books brings it over.' : 'No books you can see.'}
          </p>
        )}
        {move.error ? <p role="alert" className="text-sm text-destructive">{move.error.message}</p> : null}

        {waiting.length ? (
          <div className="rounded-md border border-dashed border-border p-3" data-testid="organisation-waiting">
            <p className="text-sm font-medium">Waiting for a book</p>
            <p className="text-xs text-muted-foreground">
              A seat is held in a book, so these go out the moment this shelf has one. Nothing has been emailed yet.
            </p>
            <ul className="mt-2 space-y-1">
              {waiting.map((note) => (
                <li key={note.id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid="organisation-waiting-row">
                  <span className="min-w-0 truncate">
                    <span className="font-medium">{note.email}</span>
                    <span className="text-muted-foreground"> · {SEAT[note.roleKey] ?? note.roleKey}</span>
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={revoke.isPending && revoke.variables === note.id}
                    onClick={() => revoke.mutate(note.id)}
                    data-testid="organisation-waiting-revoke"
                  >
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
            {revoke.error ? <p role="alert" className="text-sm text-destructive">{revoke.error.message}</p> : null}
          </div>
        ) : null}

        {inviting ? (
          <form
            className="space-y-2 rounded-md border border-border bg-muted/30 p-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (email.trim()) invite.mutate();
            }}
          >
            <p className="text-sm">
              {organisation.books.length === 0
                ? 'No books on this shelf yet, so the invitation waits here and the first book you create sends it. The Auditor seat sees everything in a book and changes nothing.'
                : 'One invitation, every book on this shelf you may invite into. The Auditor seat sees everything in each book and changes nothing.'}
            </p>
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-[14rem] flex-1">
                <Input
                  label="Email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ca@firm.in"
                  error={invite.error instanceof ApiError ? invite.error.fieldError('email') : undefined}
                  data-testid="organisation-invite-email"
                />
              </div>
              <label className="text-sm">
                <span className="mb-1.5 block font-medium">As</span>
                <select
                  className="min-h-11 rounded-md border border-input bg-background px-3 text-base sm:min-h-9 sm:text-sm"
                  value={roleKey}
                  onChange={(e) => setRoleKey(e.target.value as 'auditor' | 'member' | 'admin')}
                  data-testid="organisation-invite-role"
                >
                  <option value="auditor">Auditor</option>
                  <option value="member">Member</option>
                  <option value="admin">Administrator</option>
                </select>
              </label>
              <Button type="submit" loading={invite.isPending} data-testid="organisation-invite-send">
                Invite
              </Button>
            </div>
            {invite.error && !(invite.error instanceof ApiError && invite.error.fieldError('email')) ? (
              <p role="alert" className="text-sm text-destructive">{invite.error.message}</p>
            ) : null}
            {invited?.pending ? (
              <p className="text-sm" data-testid="organisation-invite-result">
                <span className="font-medium">{invited.pending.email}</span> is waiting for this shelf&rsquo;s first book, as{' '}
                {SEAT[invited.pending.roleKey] ?? invited.pending.roleKey}. Nothing has been emailed yet.
              </p>
            ) : invited ? (
              <ul className="space-y-1 text-sm" data-testid="organisation-invite-result">
                {invited.books.map((book) => (
                  <li key={book.orgId}>
                    <span className="font-medium">{book.name}</span>:{' '}
                    {book.outcome === 'invited' ? 'invited' : book.outcome === 'already' ? 'already a member' : (book.reason ?? 'not yours to invite into')}
                    {book.link ? (
                      <>
                        {' '}
                        · no email server, so pass on this link:{' '}
                        <code className="break-all rounded bg-background px-1 text-xs">{book.link}</code>
                      </>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </form>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** The card on Settings that leads here. */
export function OrganisationSettingsCard() {
  const active = useActiveOrganisation();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="size-4 text-muted-foreground" aria-hidden />
          Organisation
        </CardTitle>
      </CardHeader>
      <CardContent className="flex items-center justify-between gap-4 text-sm">
        <p className="text-muted-foreground">
          {active ? (
            <>
              This book is in <strong className="font-medium text-foreground">{active.name}</strong>. Organisations group books; a demo shelf and a real one stay apart.
            </>
          ) : (
            'The shelves your books stand on.'
          )}
        </p>
        <Button variant="outline" asChild>
          <Link to="/settings/organisations">Manage organisations</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
